import { COMMERCIAL_ROUNDING, MONEY_SCALE, decimal, divide, money, toString, } from '@aeris/money';
import { APPROVED_ART_STATUS, PRE_FACTORY_STAGES, PRODUCTION_WORK_STAGES, STAGE_NEEDING_PRINTER, UNIFORM_LIMITS, UNIFORM_SIZES, isUniformSize, } from "./entities.js";
const countOf = (entries) => entries.reduce((total, entry) => total + entry.quantity, 0);
/**
 * Quantas peças o pedido tem: a grade mais os personalizados.
 *
 * É a fórmula da planilha da loja (`I21+I34+N34+T34`): a peça personalizada
 * com nome e número NÃO está dentro da grade, é somada a ela.
 */
export const piecesOf = (grade, personalization) => countOf(grade) + countOf(personalization);
/**
 * O valor médio de cada peça — o "TKT médio/peça" da planilha.
 *
 * Total do pedido (frete incluso, como a loja calcula: 3265 ÷ 91 = 35,88)
 * dividido pelas peças, em `@aeris/money` e arredondado no centavo. Nulo sem
 * peça: a grade ainda não chegou, e zero seria uma mentira.
 */
export const ticketPerPiece = (total, pieces) => {
    if (!Number.isSafeInteger(pieces) || pieces <= 0)
        return null;
    return toString(divide(money(total), decimal(BigInt(pieces), 0), MONEY_SCALE, COMMERCIAL_ROUNDING));
};
/**
 * A data de despacho já passou e o pedido ainda está na fila.
 *
 * As duas datas são `AAAA-MM-DD` no fuso da loja — quem chama resolve "hoje"
 * no fuso certo, porque às 22h de São Paulo já é amanhã em UTC. Comparar o
 * texto é comparar a data: o formato é fixo e ordena.
 */
export const isLate = (dispatchDate, today) => dispatchDate !== null && dispatchDate < today;
const DAY_MS = 24 * 60 * 60 * 1_000;
/** Dias inteiros desde a última troca de etapa. */
export const daysInStage = (changedAt, now) => Math.max(0, Math.floor((now.getTime() - changedAt.getTime()) / DAY_MS));
/**
 * A troca de etapa pode acontecer?
 *
 * `from` é a etapa que a tela mostrava; `current`, a que está gravada. Quando
 * divergem, a troca é recusada em vez de aplicada: o chão de fábrica e o
 * escritório mexem na mesma fila, e "eu pus em costurando" não pode apagar em
 * silêncio o "voltou para imprimir" de outra pessoa.
 *
 * Ir para a fábrica pede a arte aprovada (F171): imprimir o que o cliente não
 * aprovou é tecido e tinta jogados fora. Vale saindo da arte e também do
 * atendimento (F228) — pular a arte não pula a aprovação. `force` passa por
 * cima — e só quem gerencia a produção pode pedir isso, o que a rota confere
 * antes.
 */
export const checkStageMove = (input) => {
    if (input.current !== input.from)
        return 'stage_changed';
    if (input.from === input.to)
        return 'same_stage';
    if (entersFactory(input.from, input.to) &&
        input.artStatus !== APPROVED_ART_STATUS &&
        input.force !== true) {
        return 'art_not_approved';
    }
    if (input.to === STAGE_NEEDING_PRINTER && input.printerCount === 0) {
        return 'printer_required';
    }
    return null;
};
const isPreFactory = (stage) => PRE_FACTORY_STAGES.includes(stage);
/**
 * A troca manda para a fábrica um pedido que estava no atendimento ou na arte
 * (F228). Entre o atendimento e a arte, nas duas direções, não é fábrica.
 */
export const entersFactory = (from, to) => isPreFactory(from) && !isPreFactory(to);
/** Esta pessoa trabalha nesta parte do pedido? */
export const canWork = (actor, fn) => actor.manages || actor.functions.includes(fn);
/**
 * Qual função move o pedido de uma etapa para outra.
 *
 * Tirar do atendimento é de quem atende — é a atendente que diz "pode ir
 * para a arte" (F228); pular a arte, só quem gerencia. Devolver para o atendimento, também do designer: é ele
 * quem vê que falta alguma coisa do cliente. Tirar da arte é de quem desenha —
 * é o designer que diz "está pronto para imprimir". Devolver para a arte,
 * também da produção: é o operador que vê o erro na tela da máquina. Entre as
 * etapas da fábrica, só a produção.
 */
export const stageMoveFunctions = (from, to) => {
    if (from === 'atendimento')
        return to === 'arte' ? ['atendimento'] : [];
    if (to === 'atendimento')
        return ['atendimento', 'arte'];
    if (from === 'arte')
        return ['arte'];
    if (to === 'arte')
        return ['arte', 'producao'];
    return ['producao'];
};
export const canMoveStage = (actor, from, to) => actor.manages ||
    stageMoveFunctions(from, to).some((fn) => actor.functions.includes(fn));
/**
 * Esta pessoa vê o dinheiro do pedido (F220)?
 *
 * O dono da 4Play, em 02/10: o designer via valor "onde só quem é dono,
 * administrador, financeiro" deveria ver. Quem fica sem o valor é quem a
 * Equipe da produção pôs SÓ na arte e/ou na fábrica: o valor, o pago, o
 * ticket por peça e o preço de cada item.
 *
 * - quem gerencia vê;
 * - quem atende vê — é ela quem combina preço e recebe o sinal;
 * - quem não tem função nenhuma não é da equipe da produção: é o vendedor de
 *   sempre, e continua vendo o que o papel dele sempre viu.
 */
export const seesMoney = (actor) => actor.manages ||
    actor.functions.length === 0 ||
    actor.functions.includes('atendimento');
export const UNIFORM_SECTORS = [
    'atendimento',
    'arte',
    'producao',
    'expedicao',
];
const isWorkStage = (stage) => PRODUCTION_WORK_STAGES.includes(stage);
/**
 * Os quatro selos do pedido: onde cada setor está.
 *
 * Derivados, nunca gravados — a grade conferida, o status da arte, a etapa e
 * o status do pedido de vendas já dizem tudo, e um quinto lugar para a mesma
 * informação só serviria para discordar dos outros quatro.
 */
export const orderSectors = (order) => ({
    atendimento: order.stage === 'atendimento'
        ? 'andamento'
        : order.gradeChecked
            ? 'feito'
            : 'pendente',
    arte: order.artStatus === APPROVED_ART_STATUS
        ? 'feito'
        : order.artStatus === 'aguardando'
            ? 'pendente'
            : 'andamento',
    producao: order.fulfilled || order.stage === 'pronto'
        ? 'feito'
        : isWorkStage(order.stage)
            ? 'andamento'
            : 'pendente',
    expedicao: order.fulfilled
        ? 'feito'
        : order.stage === 'pronto'
            ? 'andamento'
            : 'pendente',
});
/**
 * A grade gravada de volta na forma da planilha: peça × tamanho.
 *
 * O banco guarda uma linha por tamanho (é o que deixa somar e filtrar); a tela
 * e o papel desenham uma linha por peça. As linhas saem pela `position`, que é
 * a ordem em que a pessoa as escreveu.
 */
export const gradeMatrix = (entries) => {
    const byRow = new Map();
    const used = new Set();
    for (const entry of entries) {
        const key = `${String(entry.position)}\u0000${entry.block}\u0000${entry.piece}`;
        const existing = byRow.get(key);
        const quantities = existing?.quantities ?? {};
        quantities[entry.size] = (quantities[entry.size] ?? 0) + entry.quantity;
        used.add(entry.size);
        byRow.set(key, {
            quantities,
            row: {
                block: entry.block,
                piece: entry.piece,
                position: entry.position,
                quantities,
                total: (existing?.row.total ?? 0) + entry.quantity,
            },
        });
    }
    const rows = [...byRow.values()]
        .map((value) => value.row)
        .sort((left, right) => left.position - right.position);
    return {
        sizes: UNIFORM_SIZES.filter((size) => used.has(size)),
        rows,
        total: countOf(entries),
    };
};
const QUANTITY = /^\d{1,6}$/;
/**
 * Lê a lista de personalizados colada de uma planilha.
 *
 * As colunas seguem a ordem da grade da loja — **Nome, Peça, Número, Qtde,
 * Tamanho** — separadas por tabulação (o que o Excel e o Google Planilhas põem
 * na área de transferência) ou por ponto e vírgula. A tela desenha a mesma
 * ordem, então copiar da tela e colar de volta dá a mesma lista.
 *
 * Posicional de propósito: adivinhar qual célula é o tamanho não funciona
 * quando o número da camisa é "10" e o tamanho infantil também. Qtde vazia é
 * uma peça. Uma linha de cabeçalho ("Nome", …) é pulada; o resto que não fecha
 * volta em `rejected` para a pessoa ver qual foi.
 */
export const parsePersonalizationPaste = (text) => {
    const rows = [];
    const rejected = [];
    text.split(/\r?\n/).forEach((line, index) => {
        if (line.trim() === '')
            return;
        const cells = (line.includes('\t') ? line.split('\t') : line.split(';')).map((cell) => cell.trim());
        const [name = '', piece = '', number = '', quantityText = '', sizeText = '',] = cells;
        if (index === 0 && name.toLowerCase() === 'nome')
            return;
        const size = sizeText.toUpperCase();
        const quantity = quantityText === '' ? 1 : Number(quantityText);
        if (name === '' ||
            name.length > UNIFORM_LIMITS.personName ||
            piece.length > UNIFORM_LIMITS.piece ||
            number.length > UNIFORM_LIMITS.personNumber ||
            (quantityText !== '' && !QUANTITY.test(quantityText)) ||
            quantity <= 0 ||
            !isUniformSize(size)) {
            rejected.push(index + 1);
            return;
        }
        rows.push({ name, piece, number, quantity, size });
    });
    return { rows, rejected };
};
//# sourceMappingURL=rules.js.map